class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems && cartItems.updateQuantity) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

// Main Cart Items Component
class CartItems extends HTMLElement {
  constructor() {
    super();
    
    // Define constants if not already defined
    if (typeof ON_CHANGE_DEBOUNCE_TIMER === 'undefined') {
      window.ON_CHANGE_DEBOUNCE_TIMER = 300;
    }
    if (typeof PUB_SUB_EVENTS === 'undefined') {
      window.PUB_SUB_EVENTS = {
        cartUpdate: 'cart-update'
      };
    }

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || 
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, window.ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    // Initialize pub/sub if not available
    if (typeof subscribe === 'undefined') {
      window.subscribe = (event, callback) => {
        document.addEventListener(event, (e) => callback(e.detail));
        return () => document.removeEventListener(event, callback);
      };
    }
    
    if (typeof publish === 'undefined') {
      window.publish = (event, data) => {
        document.dispatchEvent(new CustomEvent(event, { detail: data }));
      };
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    
    // Initialize strings if not available
    if (typeof window.quickOrderListStrings === 'undefined') {
      window.quickOrderListStrings = {
        min_error: 'Minimum quantity is [min]',
        max_error: 'Maximum quantity is [max]',
        step_error: 'Quantity must be in increments of [step]'
      };
    }
    
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    if (event.target.classList.contains('quantity__input')) {
      this.validateQuantity(event);
    }
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      this.fetchCartDrawerUpdate();
    } else {
      this.fetchMainCartUpdate();
    }
  }

  fetchCartDrawerUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        
        selectors.forEach(selector => {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        });
        
        // Force sync with checkout
        this.syncCartWithCheckout();
      })
      .catch((e) => {
        console.error('Cart drawer update error:', e);
        this.showError('Failed to update cart. Please refresh the page.');
      });
  }

  fetchMainCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty) {
          this.innerHTML = sourceQty.innerHTML;
        }
        
        // Force sync with checkout
        this.syncCartWithCheckout();
      })
      .catch((e) => {
        console.error('Main cart update error:', e);
        this.showError('Failed to update cart. Please refresh the page.');
      });
  }

  syncCartWithCheckout() {
    // Force Shopify to recognize cart changes by making a direct API call
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('Cart synced with checkout:', cart);
        // Dispatch custom event for other components
        document.dispatchEvent(new CustomEvent('cart:synced', { 
          detail: { cart }
        }));
      })
      .catch(error => {
        console.warn('Cart sync warning:', error);
      });
  }

  getSectionsToRender() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    
    const sections = [
      {
        id: 'main-cart-items',
        section: mainCartItems ? mainCartItems.dataset.id : 'main-cart-items',
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    ];

    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    // Initialize fetchConfig if not available
    const fetchConfig = window.fetchConfig || (() => ({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': `application/json`
      }
    }));

    // Initialize routes if not available
    const routes = window.routes || {
      cart_change_url: '/cart/change.js',
      cart_url: '/cart'
    };

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        // Update all sections
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (sectionElement) {
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            const newContent = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
            if (newContent) {
              elementToReplace.innerHTML = newContent;
            }
          }
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        
        // Initialize cart strings if not available
        if (typeof window.cartStrings === 'undefined') {
          window.cartStrings = {
            error: 'There was an error while updating your cart.',
            quantityError: 'Sorry, we do not have enough of this item in stock. Only [quantity] left.'
          };
        }

        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement?.value || 0)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
          } else {
            lineItem.querySelector(`[name="${name}"]`).focus();
          }
        } else if (parsedState.item_count === 0) {
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) {
            const emptyElement = cartDrawerWrapper.querySelector('.drawer__inner-empty');
            if (emptyElement) {
              trapFocus(emptyElement, emptyElement.querySelector('a'));
            }
          }
        } else if (document.querySelector('.cart-item')) {
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        }

        // Force sync with checkout
        this.syncCartWithCheckout();

        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-items', 
          cartData: parsedState, 
          variantId: variantId 
        });
      })
      .catch((error) => {
        console.error('Cart update error:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings?.error || 'There was an error updating your cart.';
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) {
        errorText.innerHTML = message;
      }
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    try {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const selectedElement = selector ? parsedHtml.querySelector(selector) : parsedHtml.body;
      return selectedElement ? selectedElement.innerHTML : html;
    } catch (error) {
      console.error('Error parsing HTML:', error);
      return html;
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => {
      if (overlay) overlay.classList.remove('hidden');
    });

    document.activeElement?.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => {
      if (overlay) overlay.classList.add('hidden');
    });
    cartDrawerItemElements.forEach((overlay) => {
      if (overlay) overlay.classList.add('hidden');
    });
  }

  showError(message) {
    // Simple error display implementation
    const errorElement = document.createElement('div');
    errorElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 1rem;
      border-radius: 4px;
      z-index: 10000;
      max-width: 300px;
    `;
    errorElement.textContent = message;
    document.body.appendChild(errorElement);

    setTimeout(() => {
      if (errorElement.parentNode) {
        errorElement.parentNode.removeChild(errorElement);
      }
    }, 5000);
  }
}

customElements.define('cart-items', CartItems);

// Cart Note Component
if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        // Initialize routes if not available
        const routes = window.routes || {
          cart_update_url: '/cart/update.js'
        };

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { 
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body 
            }).catch(error => {
              console.error('Error updating cart note:', error);
            });
          }, window.ON_CHANGE_DEBOUNCE_TIMER || 300)
        );
      }
    }
  );
}

// Utility Functions (if not already defined)
if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
  window.debounce = debounce;
}

if (typeof trapFocus === 'undefined') {
  function trapFocus(element, focusElement = null) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusElement || focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    function handleTabKey(e) {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    element.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    // Return cleanup function
    return () => element.removeEventListener('keydown', handleTabKey);
  }
  window.trapFocus = trapFocus;
}

if (typeof removeTrapFocus === 'undefined') {
  function removeTrapFocus(elementToFocus = null) {
    elementToFocus?.focus();
  }
  window.removeTrapFocus = removeTrapFocus;
}

if (typeof onKeyUpEscape === 'undefined') {
  function onKeyUpEscape(e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = e.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement?.setAttribute('aria-expanded', false);
    summaryElement?.focus();
  }
  window.onKeyUpEscape = onKeyUpEscape;
}

// Enhanced Cart Synchronization Manager
class CartSyncManager {
  constructor() {
    this.isSyncing = false;
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', this.syncWithCheckout.bind(this));
    document.addEventListener('cart:synced', this.validateCheckoutSync.bind(this));
    
    // Sync on page load
    this.syncWithCheckout();
  }

  async syncWithCheckout() {
    if (this.isSyncing) return;
    
    this.isSyncing = true;
    
    try {
      // Force cart refresh
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      
      // Update all cart-related elements
      this.updateCartElements(cartData);
      
      // Verify checkout synchronization
      await this.verifyCheckoutSync(cartData);
      
    } catch (error) {
      console.error('Cart sync error:', error);
    } finally {
      this.isSyncing = false;
    }
  }

  updateCartElements(cartData) {
    // Update cart count
    const countElements = document.querySelectorAll('.cart-count, #cart-count');
    countElements.forEach(el => {
      if (el) {
        el.textContent = cartData.item_count;
        el.style.display = cartData.item_count > 0 ? 'flex' : 'none';
      }
    });

    // Update cart total
    const totalElements = document.querySelectorAll('.cart-total__amount, .totals__subtotal-value');
    totalElements.forEach(el => {
      if (el) {
        el.textContent = this.formatMoney(cartData.total_price);
      }
    });

    // Dispatch update event
    document.dispatchEvent(new CustomEvent('cart:visual-update', {
      detail: { cart: cartData }
    }));
  }

  async verifyCheckoutSync(cartData) {
    try {
      // Make a lightweight call to checkout to ensure synchronization
      const response = await fetch('/checkout', { method: 'HEAD' });
      if (response.ok) {
        console.log('Cart successfully synchronized with checkout');
      }
    } catch (error) {
      console.warn('Checkout sync verification failed:', error);
    }
  }

  validateCheckoutSync(event) {
    const cart = event.detail.cart;
    console.log('Cart validation:', {
      itemCount: cart.item_count,
      totalPrice: cart.total_price,
      items: cart.items.map(item => ({
        id: item.id,
        quantity: item.quantity,
        title: item.title
      }))
    });
  }

  formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  }
}

// Initialize cart sync manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartSyncManager = new CartSyncManager();
  });
} else {
  window.cartSyncManager = new CartSyncManager();
}